import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { LanguageToggle } from "LanguageToggle";
import { Cell, Row, GridCustomColumns } from "../Grid";
import { Section } from "../Section";
import styles from "./Footer.module.scss";

const defaultLabels = {
	ourSite: "Our Site",
};

const Footer = ({
	className,
	labels: providedLabels,
	columns,
	renderLinks,
	renderImageLinks,
	corps,
	flags,
	lang,
	languagePages,
	handleChangeLanguage,
	copyrightText,
}) => {
	const labels = { ...defaultLabels, ...providedLabels };

	return (
		<Section as="footer" className={cx(className, styles.footer)}>
			{columns?.length && (
				<Row customCols={GridCustomColumns["md3-lg6"]} className={styles.footerLinksRow}>
					{columns?.map(column => (
						<Cell key={column.id} className={styles.footerLinksColumn}>
							<div className={styles.footerSection}>
								<p>
									<strong>{column.title}</strong>
								</p>
								<ul>
									{column.links.map(item => (
										<li key={item.id}>{renderLinks(item)}</li>
									))}
								</ul>
							</div>
						</Cell>
					))}
				</Row>
			)}
			{(corps?.length || flags?.length || copyrightText) && (
				<div className={styles.container}>
					<div className={styles.footerBottom}>
						{corps?.length > 0 && (
							<div className={styles.footerCorps}>
								{corps?.map(item => (
									<React.Fragment key={item.id}>
										{renderImageLinks(item, {
											className: styles.footerCorpsLink,
											image: (
												// eslint-disable-next-line jsx-a11y/img-redundant-alt
												<img
													src={item?.image?.url}
													alt={item?.image?.alt}
													className={styles.footerCorpsImage}
												/>
											),
										})}
									</React.Fragment>
								))}
							</div>
						)}
						{flags?.length > 0 && (
							<div className={styles.footerSites}>
								<div className={styles.footerSites__title}>{labels.ourSite}</div>
								{flags?.map(item => (
									<React.Fragment key={item.id}>
										{renderImageLinks(item, {
											// eslint-disable-next-line jsx-a11y/img-redundant-alt
											image: <img src={item?.image?.url} alt={item?.image.alt} />,
										})}
									</React.Fragment>
								))}
							</div>
						)}
						{languagePages?.length && (
							<div className={styles.footerLang}>
								<LanguageToggle
									currentLanguage={lang}
									languagePages={languagePages}
									handleChangeLanguage={handleChangeLanguage}
									classNames={{
										currentLanguage: styles.langToggleCurrent,
										altLanguage: styles.langToggleAlt,
									}}
								/>
							</div>
						)}
					</div>
					{copyrightText && (
						<div className={styles.footerCopyright}>
							<p>{copyrightText}</p>
						</div>
					)}
				</div>
			)}
		</Section>
	);
};

Footer.propTypes = {
	className: PropTypes.string,
	labels: PropTypes.shape({ ourSite: PropTypes.node }),
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			links: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.string.isRequired,
					title: PropTypes.string.isRequired,
					url: PropTypes.string.isRequired,
				}).isRequired
			).isRequired,
		}).isRequired
	),
	/**
	 * Function called with the each link item to render it.
	 *
	 * `renderLinks: link => {}`
	 */
	renderLinks: PropTypes.func,
	/**
	 * Function called with the each image link item to render it.
	 *
	 * The image argument is an image returned returned as
	 *
	 * `<img`
	 *
	 * `src={link?.image?.url}`
	 *
	 * `alt={link?.image?.alt}`
	 *
	 * `className={styles.footerCorpsImage}/>`.
	 *
	 * You can ignore this argument and supply your own logic for rendering the image using the `link` object provided.
	 *
	 * `renderImageLinks: (link, image) => {}`
	 */
	renderImageLinks: PropTypes.func,
	corps: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			link: PropTypes.shape({
				url: PropTypes.string.isRequired,
				target: PropTypes.string.isRequired,
			}).isRequired,
			image: PropTypes.shape({
				url: PropTypes.string.isRequired,
				alt: PropTypes.string.isRequired,
			}).isRequired,
		})
	),
	flags: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			link: PropTypes.shape({
				url: PropTypes.string.isRequired,
				target: PropTypes.string.isRequired,
			}).isRequired,
			image: PropTypes.shape({
				url: PropTypes.string.isRequired,
				alt: PropTypes.string.isRequired,
			}).isRequired,
		})
	),
	lang: PropTypes.string,
	languagePages: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			lang: PropTypes.string.isRequired,
			path: PropTypes.string.isRequired,
		}).isRequired
	),
	/**
	 * Function called with the clicked languagePages item
	 *
	 * `handleChangeLanguage: page => {}`
	 */
	handleChangeLanguage: PropTypes.func,
	copyrightText: PropTypes.node.isRequired,
};

Footer.defaultProps = {
	className: undefined,
	labels: defaultLabels,
	columns: undefined,
	renderLinks: link => (
		<a href={link.url} target={link.target}>
			{link.title}
		</a>
	),
	renderImageLinks: (link, { className, image }) => (
		<a className={className} href={link.url} target={link.target}>
			{image}
		</a>
	),
	corps: undefined,
	flags: undefined,
	lang: undefined,
	languagePages: undefined,
	handleChangeLanguage: () => {},
};

export default Footer;
export { Footer };
